import { useEffect, useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { useCalendarUIStore, type CalendarViewMode } from '../store/calendarUIStore';
import { googleCalendarService, type CalendarListEntry } from '../services/googleCalendarService';
import { dataRepository } from '../repository/DataRepository';
import type { PKMObject } from '../models/types';
import AddEventModal from './AddEventModal';
import './CalendarSidebarSection.css';

const VIEW_OPTIONS: { value: CalendarViewMode; label: string }[] = [
  { value: 'day', label: 'Day' },
  { value: '3day', label: '3 Day' },
  { value: 'week', label: 'Week' },
  { value: '2week', label: '2 Week' },
  { value: 'month', label: 'Month' },
];

const DAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

function buildMonthGrid(monthAnchor: Date): Date[] {
  const firstOfMonth = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1);
  const startOffset = firstOfMonth.getDay(); // Sun = 0
  const start = new Date(firstOfMonth);
  start.setDate(start.getDate() - startOffset);
  const days: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

export default function CalendarSidebarSection() {
  const viewMode = useCalendarUIStore((s) => s.viewMode);
  const setViewMode = useCalendarUIStore((s) => s.setViewMode);
  const currentDate = useCalendarUIStore((s) => s.currentDate);
  const setCurrentDate = useCalendarUIStore((s) => s.setCurrentDate);
  const selectedCalendars = useCalendarUIStore((s) => s.selectedCalendars);
  const setSelectedCalendars = useCalendarUIStore((s) => s.setSelectedCalendars);
  const filterByProject = useCalendarUIStore((s) => s.filterByProject);
  const setFilterByProject = useCalendarUIStore((s) => s.setFilterByProject);
  const filterByArea = useCalendarUIStore((s) => s.filterByArea);
  const setFilterByArea = useCalendarUIStore((s) => s.setFilterByArea);
  const showCompleted = useCalendarUIStore((s) => s.showCompleted);
  const setShowCompleted = useCalendarUIStore((s) => s.setShowCompleted);
  const showTasks = useCalendarUIStore((s) => s.showTasks);
  const setShowTasks = useCalendarUIStore((s) => s.setShowTasks);
  const includeGoogleCalendar = useCalendarUIStore((s) => s.includeGoogleCalendar);
  const setIncludeGoogleCalendar = useCalendarUIStore((s) => s.setIncludeGoogleCalendar);

  const [showAddEvent, setShowAddEvent] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [viewExpanded, setViewExpanded] = useState(true);
  const [jumperExpanded, setJumperExpanded] = useState(true);
  const [calendarsExpanded, setCalendarsExpanded] = useState(true);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const [googleCalendars, setGoogleCalendars] = useState<CalendarListEntry[]>([]);
  const [projects, setProjects] = useState<PKMObject[]>([]);
  const [areas, setAreas] = useState<PKMObject[]>([]);
  const [jumperMonth, setJumperMonth] = useState<Date>(() => new Date(currentDate));

  // Keep jumper month in sync when currentDate changes from elsewhere
  useEffect(() => {
    setJumperMonth((prev) => {
      if (prev.getFullYear() === currentDate.getFullYear() && prev.getMonth() === currentDate.getMonth()) {
        return prev;
      }
      return new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    });
  }, [currentDate]);

  // Load Google calendars list
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!googleCalendarService.isConnected()) return;
      try {
        const list = await googleCalendarService.listCalendars();
        if (!cancelled) setGoogleCalendars(list);
      } catch (err) {
        console.error('[CalendarSidebarSection] Failed to load calendars:', err);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Load projects + areas for filters
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const all = await dataRepository.getAllObjects();
        if (cancelled) return;
        setProjects(all.filter((o) => o.type === 'project'));
        setAreas(all.filter((o) => o.type === 'area'));
      } catch (err) {
        console.error('[CalendarSidebarSection] Failed to load filter options:', err);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const monthGrid = useMemo(() => buildMonthGrid(jumperMonth), [jumperMonth]);
  const today = useMemo(() => new Date(), []);
  const monthLabel = jumperMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const handleCalendarToggle = (calId: string, checked: boolean) => {
    if (checked) {
      let next: string[];
      if (selectedCalendars.length === 0) {
        next = googleCalendars.map((c) => c.id);
      } else {
        next = [...selectedCalendars, calId];
      }
      if (next.length === googleCalendars.length) next = [];
      setSelectedCalendars(next);
    } else {
      let next: string[];
      if (selectedCalendars.length === 0) {
        next = googleCalendars.filter((c) => c.id !== calId).map((c) => c.id);
      } else {
        next = selectedCalendars.filter((id) => id !== calId);
      }
      setSelectedCalendars(next);
    }
  };

  return (
    <div className="nav-section calendar-sidebar-section">
      <div className="nav-section-title" onClick={() => setExpanded(!expanded)}>
        <span className="collapse-icon">{expanded ? '▼' : '▶'}</span>
        Calendar
      </div>

      {expanded && (
        <div className="calendar-sidebar-body">
          {/* + New event */}
          <button
            type="button"
            className="cal-add-event-btn"
            onClick={() => setShowAddEvent(true)}
          >
            <Plus size={14} />
            <span>New event</span>
          </button>

          {/* View picker */}
          <div className="cal-subsection">
            <button
              type="button"
              className="cal-subsection-title"
              onClick={() => setViewExpanded(!viewExpanded)}
            >
              {viewExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              <span>View</span>
            </button>
            {viewExpanded && (
              <div className="cal-view-picker">
                {VIEW_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`cal-view-btn ${viewMode === opt.value ? 'active' : ''}`}
                    onClick={() => setViewMode(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Mini date jumper */}
          <div className="cal-subsection">
            <button
              type="button"
              className="cal-subsection-title"
              onClick={() => setJumperExpanded(!jumperExpanded)}
            >
              {jumperExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              <span>Jump to date</span>
            </button>
            {jumperExpanded && (
              <div className="cal-jumper">
                <div className="cal-jumper-header">
                  <button
                    type="button"
                    className="cal-jumper-nav"
                    onClick={() => setJumperMonth(new Date(jumperMonth.getFullYear(), jumperMonth.getMonth() - 1, 1))}
                    aria-label="Previous month"
                  >‹</button>
                  <span className="cal-jumper-label">{monthLabel}</span>
                  <button
                    type="button"
                    className="cal-jumper-nav"
                    onClick={() => setJumperMonth(new Date(jumperMonth.getFullYear(), jumperMonth.getMonth() + 1, 1))}
                    aria-label="Next month"
                  >›</button>
                </div>
                <div className="cal-jumper-grid">
                  {DAY_LETTERS.map((d, i) => (
                    <div key={`dl-${i}`} className="cal-jumper-day-letter">{d}</div>
                  ))}
                  {monthGrid.map((d) => {
                    const inMonth = d.getMonth() === jumperMonth.getMonth();
                    const isCurrent = isSameDay(d, currentDate);
                    const isToday = isSameDay(d, today);
                    return (
                      <button
                        key={d.toISOString()}
                        type="button"
                        className={`cal-jumper-day ${inMonth ? '' : 'out-of-month'} ${isCurrent ? 'current' : ''} ${isToday ? 'today' : ''}`}
                        onClick={() => setCurrentDate(new Date(d))}
                      >
                        {d.getDate()}
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="cal-jumper-today-btn"
                  onClick={() => {
                    const now = new Date();
                    setCurrentDate(now);
                    setJumperMonth(new Date(now.getFullYear(), now.getMonth(), 1));
                  }}
                >
                  Today
                </button>
              </div>
            )}
          </div>

          {/* Visible calendars */}
          {googleCalendars.length > 0 && (
            <div className="cal-subsection">
              <button
                type="button"
                className="cal-subsection-title"
                onClick={() => setCalendarsExpanded(!calendarsExpanded)}
              >
                {calendarsExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                <span>Calendars</span>
              </button>
              {calendarsExpanded && (
                <div className="cal-calendar-list">
                  <label className="cal-calendar-row">
                    <input
                      type="checkbox"
                      checked={includeGoogleCalendar}
                      onChange={(e) => setIncludeGoogleCalendar(e.target.checked)}
                    />
                    <span className="cal-calendar-name">Google Calendar</span>
                  </label>
                  {includeGoogleCalendar && googleCalendars.map((cal) => {
                    const checked = selectedCalendars.length === 0 || selectedCalendars.includes(cal.id);
                    return (
                      <label key={cal.id} className="cal-calendar-row indented">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => handleCalendarToggle(cal.id, e.target.checked)}
                        />
                        <span
                          className="cal-calendar-dot"
                          style={{ backgroundColor: cal.backgroundColor || 'var(--accent-primary)' }}
                        />
                        <span className="cal-calendar-name" title={cal.summary}>{cal.summary}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Filters */}
          <div className="cal-subsection">
            <button
              type="button"
              className="cal-subsection-title"
              onClick={() => setFiltersExpanded(!filtersExpanded)}
            >
              {filtersExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              <span>Filters</span>
            </button>
            {filtersExpanded && (
              <div className="cal-filters">
                <label className="cal-filter-row">
                  <span className="cal-filter-label">Project</span>
                  <select
                    value={filterByProject || ''}
                    onChange={(e) => setFilterByProject(e.target.value || undefined)}
                  >
                    <option value="">All</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </label>
                <label className="cal-filter-row">
                  <span className="cal-filter-label">Area</span>
                  <select
                    value={filterByArea || ''}
                    onChange={(e) => setFilterByArea(e.target.value || undefined)}
                  >
                    <option value="">All</option>
                    {areas.map((a) => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </label>
                <label className="cal-filter-row checkbox">
                  <input
                    type="checkbox"
                    checked={showTasks}
                    onChange={(e) => setShowTasks(e.target.checked)}
                  />
                  <span>Show tasks</span>
                </label>
                <label className="cal-filter-row checkbox">
                  <input
                    type="checkbox"
                    checked={showCompleted}
                    onChange={(e) => setShowCompleted(e.target.checked)}
                    disabled={!showTasks}
                  />
                  <span>Show completed tasks</span>
                </label>
              </div>
            )}
          </div>
        </div>
      )}

      {showAddEvent && <AddEventModal onClose={() => setShowAddEvent(false)} />}
    </div>
  );
}
